import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X, Mic2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";

const navLinks = [
  { href: "#o-nas", label: "O nas" },
  { href: "#oferta", label: "Oferta" },
  { href: "#dlaczego-my", label: "Dlaczego my?" },
  { href: "#kontakt", label: "Kontakt" },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const getHref = (hash: string) => isHome ? hash : `/${hash}`;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
      (scrolled || !isHome)
        ? "bg-white/90 backdrop-blur-xl shadow-lg shadow-black/5 py-3" 
        : "bg-transparent py-5"
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          <a href="/" className={`flex items-center gap-2.5 text-xl font-montserrat font-bold transition-colors duration-300 ${
            (scrolled || !isHome) ? "text-primary" : "text-white"
          }`}>
            <div className="w-9 h-9 rounded-xl bg-brand-purple flex items-center justify-center shadow-lg shadow-brand-purple/30">
              <Mic2 className="w-5 h-5 text-white" />
            </div>
            WystepKomediowy.pl
          </a>

          <Button
            variant="ghost"
            className={`md:hidden ${(scrolled || !isHome) ? "text-primary" : "text-white"}`}
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Zamknij menu" : "Otwórz menu"}
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={getHref(link.href)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  (scrolled || !isHome)
                    ? "text-primary/70 hover:text-primary hover:bg-gray-100" 
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={getHref("#kontakt")}
              className="ml-2 px-5 py-2 rounded-full bg-brand-purple text-white text-sm font-medium hover:bg-brand-purple-dark transition-all duration-300 shadow-md shadow-brand-purple/20"
            >
              Zamów występ
            </a>
          </div>

          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl shadow-2xl md:hidden border-t border-gray-100"
              >
                <div className="flex flex-col p-6 gap-2">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={getHref(link.href)}
                      className="text-primary/80 hover:text-primary px-4 py-3 rounded-xl hover:bg-brand-purple/5 transition-all font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                  <a
                    href={getHref("#kontakt")}
                    className="mt-2 px-5 py-3 rounded-xl bg-brand-purple text-white text-center font-medium hover:bg-brand-purple-dark transition-all"
                    onClick={() => setIsOpen(false)}
                  >
                    Zamów występ
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
